import React, { useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { HOURS, LOCATION } from "../lib/brand";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Visit() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill out all fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — we'll reply soon!");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Couldn't send. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="visit"
      data-testid="visit-section"
      className="relative bg-[#0a0a0a] py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 max-w-2xl">
          <div className="mb-3 text-xs uppercase tracking-[0.3em] text-[#E11D2A]">Visit</div>
          <h2 className="font-display text-4xl tracking-widest text-[#F5F5DC] sm:text-5xl lg:text-6xl">
            Find the <span className="text-[#E11D2A]">Dojo</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* Map + info */}
          <div className="border border-[#27272A] bg-[#121212]">
            <div className="aspect-[16/10] w-full overflow-hidden">
              <iframe
                title="Kung Fu TCG Location"
                data-testid="location-map-iframe"
                className="h-full w-full grayscale contrast-125"
                src="https://www.openstreetmap.org/export/embed.html?bbox=-96.6230%2C33.0130%2C-96.6010%2C33.0250&layer=mapnik&marker=33.0190%2C-96.6120"
                loading="lazy"
              />
            </div>
            <div className="grid grid-cols-1 gap-6 p-8 sm:grid-cols-2">
              <div>
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#E11D2A]">
                  <MapPin className="h-4 w-4" /> Address
                </div>
                <p data-testid="address-line" className="mt-2 text-[#F5F5DC]">
                  <span className="block font-display tracking-wider text-[#E11D2A]">
                    {LOCATION.name}
                  </span>
                  {LOCATION.address}
                  <br />
                  {LOCATION.city}
                </p>
              </div>
              <div>
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#E11D2A]">
                  <Clock className="h-4 w-4" /> Hours
                </div>
                <ul className="mt-2 space-y-1 text-sm text-[#F5F5DC]">
                  {HOURS.map((h) => (
                    <li key={h.day} className="flex justify-between gap-4">
                      <span className="text-[#A1A1AA]">{h.day}</span>
                      <span>{h.hours}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#E11D2A]">
                  <Phone className="h-4 w-4" /> Phone
                </div>
                <a
                  data-testid="phone-link"
                  href={`tel:${LOCATION.phone.replace(/[^0-9+]/g, "")}`}
                  className="mt-2 inline-block text-[#F5F5DC] hover:text-[#E11D2A]"
                >
                  {LOCATION.phone}
                </a>
              </div>
              <div>
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#E11D2A]">
                  <Mail className="h-4 w-4" /> Email
                </div>
                <a
                  data-testid="email-link"
                  href={`mailto:${LOCATION.email}`}
                  className="mt-2 inline-block text-[#F5F5DC] hover:text-[#E11D2A]"
                >
                  {LOCATION.email}
                </a>
              </div>
            </div>
          </div>

          {/* Contact form */}
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="border border-[#27272A] bg-[#121212] p-8"
          >
            <h3 className="font-display text-3xl tracking-widest text-[#F5F5DC]">
              Send a Message
            </h3>
            <p className="mt-2 text-sm text-[#A1A1AA]">
              Questions about events, pre-orders, or singles? Drop us a line.
            </p>

            <div className="mt-6 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-[#A1A1AA]">Name</label>
                <Input
                  data-testid="contact-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 rounded-none border-[#27272A] bg-[#0a0a0a] text-[#F5F5DC] focus-visible:ring-[#E11D2A]"
                  placeholder="Bruce L."
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#A1A1AA]">Email</label>
                <Input
                  data-testid="contact-email-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 rounded-none border-[#27272A] bg-[#0a0a0a] text-[#F5F5DC] focus-visible:ring-[#E11D2A]"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#A1A1AA]">Message</label>
                <Textarea
                  data-testid="contact-message-input"
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="mt-1 rounded-none border-[#27272A] bg-[#0a0a0a] text-[#F5F5DC] focus-visible:ring-[#E11D2A]"
                  placeholder="Tell us what you need..."
                />
              </div>
              <Button
                data-testid="contact-submit-button"
                type="submit"
                disabled={submitting}
                className="w-full rounded-none bg-[#E11D2A] py-6 font-semibold uppercase tracking-widest text-white hover:bg-[#F02D3A] disabled:opacity-60"
              >
                {submitting ? "Sending..." : "Send Message"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
